if (!customElements.get('pdp-nav')) {
  customElements.define(
    'pdp-nav',
    class PdpNav extends HTMLElement {
      // Where the reading line sits in the viewport area below the nav (0 = right under it)
      static LINE = 0.3;

      connectedCallback() {
        // Placeholder that keeps the nav's place in the flow while the nav itself is fixed
        this.placeholder = this.closest('.pdp-nav-slot') || this.parentElement;
        this.chaptersEl = this.querySelector('[data-chapters]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onScroll = () => this.scheduleUpdate();
        // Theme editor: sections (and their markers) can be added, removed or reordered at any time
        this.onSectionsChange = () =>
          requestAnimationFrame(() => {
            this.build();
            this.scheduleUpdate();
          });
        this.onChapterClick = (event) => {
          const link = event.target.closest('.pdp-nav__chapter');
          if (!link) return;
          event.preventDefault();
          this.scrollToChapter(this.chapters[Number(link.dataset.index)]);
        };

        // The theme's --header-height is a rounded offsetHeight measured once; track the exact height so the
        // pinned nav can follow the header's real bottom edge without a sub-pixel seam (see pdp-nav.css)
        const header = document.querySelector('.section-header');
        if (header && 'ResizeObserver' in window) {
          this.headerObserver = new ResizeObserver(([entry]) => {
            const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--pdp-header-height', `${height}px`);
            this.scheduleUpdate();
          });
          this.headerObserver.observe(header);
        }

        this.chaptersEl.addEventListener('click', this.onChapterClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) =>
          document.addEventListener(type, this.onSectionsChange)
        );

        this.build();
        this.scheduleUpdate();
      }

      disconnectedCallback() {
        this.headerObserver?.disconnect();
        this.chaptersEl.removeEventListener('click', this.onChapterClick);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) =>
          document.removeEventListener(type, this.onSectionsChange)
        );
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      build() {
        const chapters = [];
        document.querySelectorAll('[data-pdp-section]').forEach((marker) => {
          if (!marker.isConnected || this.contains(marker)) return;
          const target = marker.closest('.shopify-section') || marker.parentElement;
          // A labelled section starts a chapter; unlabelled product-content sections (FAQ, ingredients, …)
          // just belong to the chapter before them, which matters for where the last chapter ends
          if (marker.dataset.pdpChapter) chapters.push({ label: marker.dataset.pdpChapter, target, members: [] });
          else if (chapters.length) chapters[chapters.length - 1].members.push(target);
        });

        this.chapters = chapters;
        this.activeIndex = null;
        this.classList.toggle('is-empty', !chapters.length);

        this.chaptersEl.replaceChildren(
          ...chapters.map((chapter, index) => {
            const link = document.createElement('a');
            link.className = 'pdp-nav__chapter';
            link.href = chapter.target.id ? `#${chapter.target.id}` : '#';
            link.dataset.index = index;

            const label = document.createElement('span');
            label.className = 'pdp-nav__chapter-label';
            label.textContent = chapter.label;
            label.dataset.text = chapter.label;

            const bar = document.createElement('span');
            bar.className = 'pdp-nav__chapter-bar';
            bar.setAttribute('aria-hidden', 'true');
            bar.append(document.createElement('span'));

            link.append(label, bar);
            chapter.link = link;
            return link;
          })
        );
      }

      scheduleUpdate() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      // Below the header while it is visible, else 0 (resolved in CSS from the header state)
      stickyTop() {
        return parseFloat(getComputedStyle(this).getPropertyValue('--pdp-sticky-top')) || 0;
      }

      update() {
        if (!this.chapters || !this.chapters.length) return;

        // Read everything first, then write, so a frame never forces more than one layout
        const navHeight = this.getBoundingClientRect().height;
        const stickyTop = this.stickyTop();
        const placeholderTop = this.placeholder.getBoundingClientRect().top;
        const navBottom = stickyTop + navHeight;
        const line = navBottom + (window.innerHeight - navBottom) * PdpNav.LINE;

        const starts = this.chapters.map((chapter) => chapter.target.getBoundingClientRect().top);
        const last = this.chapters[this.chapters.length - 1];
        const lastEnd = Math.max(...[last.target, ...last.members].map((section) => section.getBoundingClientRect().bottom));
        const ends = starts.slice(1).concat(lastEnd);

        // Like the article TOC: the active chapter is the last one whose start crossed the line,
        // so it can only move with the scroll direction and never flickers.
        let active = -1;
        starts.forEach((top, index) => {
          if (top <= line) active = index;
        });

        if (this.navHeight !== navHeight) {
          this.navHeight = navHeight;
          document.documentElement.style.setProperty('--pdp-nav-height', `${navHeight}px`);
        }
        // Pin once the nav's place in the flow reaches the top; it unpins when scrolling back above it.
        // While pinned, the placeholder holds the in-flow height measured right before, so nothing below moves.
        const fixed = placeholderTop <= stickyTop;
        if (fixed && !this.classList.contains('is-fixed')) this.placeholder.style.height = `${navHeight}px`;
        if (!fixed) this.placeholder.style.removeProperty('height');
        this.classList.toggle('is-fixed', fixed);
        this.classList.toggle('is-past', line > lastEnd);

        this.chapters.forEach((chapter, index) => {
          PdpNav.setFill(chapter.link, PdpNav.progress(line, starts[index], ends[index]));
          chapter.link.classList.toggle('is-active', index === active);
          chapter.link.classList.toggle('is-done', index < active);
          if (index === active) chapter.link.setAttribute('aria-current', 'true');
          else chapter.link.removeAttribute('aria-current');
        });

        const current = Math.max(active, 0);
        if (current !== this.activeIndex) {
          this.activeIndex = current;
          this.revealChapter(this.chapters[current].link);
        }
      }

      revealChapter(link) {
        const scroller = this.chaptersEl;
        if (scroller.scrollWidth <= scroller.clientWidth) return;
        scroller.scrollTo({
          left: link.offsetLeft - (scroller.clientWidth - link.offsetWidth) / 2,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      scrollToChapter(chapter) {
        if (!chapter) return;
        const top = chapter.target.getBoundingClientRect().top + window.scrollY;
        // The header ends up visible (pushing the nav down by its height) if it is always sticky,
        // or if it is the on-scroll-up kind and we scroll up
        const stickyType = document.querySelector('sticky-header')?.getAttribute('data-sticky-type');
        const headerVisible =
          stickyType === 'always' ||
          stickyType === 'reduce-logo-size' ||
          (stickyType === 'on-scroll-up' && top < window.scrollY);
        // Same geometry as --pdp-sticky-top in pdp-nav.css: glued 1px under the header's real bottom edge
        const root = getComputedStyle(document.documentElement);
        const themeHeight = parseFloat(root.getPropertyValue('--header-height')) || 0;
        const realHeight = parseFloat(root.getPropertyValue('--pdp-header-height')) || themeHeight;
        const navTop = stickyType ? (headerVisible ? realHeight : realHeight - themeHeight) - 1 : 0;
        window.scrollTo({
          top: Math.max(top - navTop - this.offsetHeight, 0),
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      static progress(line, start, end) {
        if (end <= start) return line >= start ? 1 : 0;
        return Math.min(Math.max((line - start) / (end - start), 0), 1);
      }

      static setFill(element, fill) {
        const value = fill.toFixed(3);
        if (element.style.getPropertyValue('--fill') !== value) element.style.setProperty('--fill', value);
      }
    }
  );
}
